/**
 * Quarantine treatment arithmetic, for the protocol page's preview only.
 *
 * Mirrors HHNodeServer src/utils/quarantine.js, which is authoritative; both
 * pin the same worked example (120 × 20cm at 0.9 L and 40 × 25cm at 1.6 L is
 * 172 L; 50 mL per 100 L of that is 86 mL). Change one, change both.
 */

const round = (n: number, dp = 2) => Math.round(n * 10 ** dp) / 10 ** dp;

/** Litres of mix for so many plants at so many litres each. */
export function litresFor(plants: number, litresPerPlant: number): number {
  return round(plants * litresPerPlant);
}

/** Amount of a chemical for so many litres of mix: the rate is per 100 L. */
export function chemicalAmount(ratePer100l: number, litres: number): number {
  return round((ratePer100l * litres) / 100, 3);
}

/** Whole tanks for so many litres, or null when either is missing. */
export function tanksFor(litres: number, tankCapacity: number): number | null {
  if (!(tankCapacity > 0) || !(litres > 0)) return null;
  return Math.ceil(litres / tankCapacity);
}

/** Drench is made up in whole steps of this many litres (Rob, 2026-10-19). */
export const MAKE_UP_STEP_L = 50;

/**
 * The tanks to make up for so many litres of drench: full tanks while more
 * than a tank is needed, then the rest rounded up to the next 50 L, never
 * past the tank. 163 L in a 400 L tank is [200]. No tank size: one make-up.
 */
export function makeUpTanks(litres: number, tankCapacity: number | null, step = MAKE_UP_STEP_L): number[] {
  if (!(litres > 0)) return [];
  const up = (l: number) => Math.ceil(round(l, 3) / step) * step;
  const cap = tankCapacity ?? 0;
  if (!(cap > 0)) return [up(litres)];
  const fills = Math.ceil(round(litres / cap, 6));
  const last = round(litres - (fills - 1) * cap, 3);
  return [...Array<number>(fills - 1).fill(cap), Math.min(up(last), cap)];
}

/** "240" hours reads "10 days"; "72" reads "72 hours". */
export function windowText(hours: number): string {
  if (hours >= 48 && hours % 24 === 0) return `${hours / 24} days`;
  return `${hours} hour${hours === 1 ? '' : 's'}`;
}

/** Spray statuses at which a treatment has gone on, or is going on: its orders can be signed off as treated. */
export const SPRAY_UNDER_WAY = ['in_progress', 'paused', 'completed'];
